/**
 * Copyright © Block, Inc. All rights reserved.
 */

import { semantic as arcadeSemanticColors } from '@arcade/web'

// Types
type SelectableAccentColor = string
type ColorWithTheme = { light: string; dark: string }

const selectableAccentColors: ColorWithTheme[] = [
  arcadeSemanticColors.accentTurquoise,
  arcadeSemanticColors.accentPurple,
  arcadeSemanticColors.accentRoyal,
  arcadeSemanticColors.accentSky,
  arcadeSemanticColors.accentPink,
  // Check and use substitutes for colors that aren't in the token file
  arcadeSemanticColors.accentAmber || arcadeSemanticColors.accentSunshine,
  arcadeSemanticColors.accentOcean || arcadeSemanticColors.accentTurquoise,
  arcadeSemanticColors.accentSunshine,
  arcadeSemanticColors.accentScarlet,
]

/**
 * Hash function for deterministic color selection
 * Uses an adaptation of the djb2 hashing algorithm
 */
const hash = (identifier: string): number => {
  // Get the UTF-16 code units, handling surrogate pairs properly
  const characterValues = Array.from(identifier.normalize()).flatMap(char => {
    const codePoints = []
    for (let i = 0; i < char.length; i++) {
      codePoints.push(char.charCodeAt(i))
    }
    return codePoints
  })

  return characterValues.reduce((lastHash, characterValue) => {
    return ((lastHash << 5) - lastHash + characterValue) | 0
  }, 0)
}

/**
 * Gets a deterministic accent color for a specific identifier
 */
export const accentColorFor = (identifier: string): ColorWithTheme => {
  const numberOfAccentColors = selectableAccentColors.length
  const index = hash(identifier) % numberOfAccentColors
  return selectableAccentColors[Math.abs(index)]
}

/**
 * Gets an accent color based on various possible identifier types
 */
export const arcadeCustomerColor = ({
  threadedCustomerToken,
  customerToken,
  fullName,
  smsNumber,
  emailAddress,
  isDarkMode = false,
}: {
  threadedCustomerToken?: string
  customerToken?: string
  fullName?: string
  smsNumber?: string
  emailAddress?: string
  isDarkMode?: boolean
}): SelectableAccentColor => {
  const identifier = threadedCustomerToken || customerToken || fullName || smsNumber || emailAddress

  if (identifier) {
    return isDarkMode ? accentColorFor(identifier).dark : accentColorFor(identifier).light
  } else {
    return isDarkMode ? arcadeSemanticColors.backgroundSubtle.dark : arcadeSemanticColors.backgroundSubtle.light
  }
}

/**
 * Gets a themed accent color based on various possible identifier types
 */
export const arcadeCustomerColorWithTheme = ({
  threadedCustomerToken,
  customerToken,
  fullName,
  smsNumber,
  emailAddress,
}: {
  threadedCustomerToken?: string
  customerToken?: string
  fullName?: string
  smsNumber?: string
  emailAddress?: string
}): ColorWithTheme => {
  const identifier = threadedCustomerToken || customerToken || fullName || smsNumber || emailAddress

  if (identifier) {
    return accentColorFor(identifier)
  } else {
    return arcadeSemanticColors.backgroundSubtle
  }
}
